import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageCircle, Send, X, User, Check, CheckCheck, Minimize2, Maximize2,
  Paperclip, Image, FileText, Download, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import { playNotificationSound, showBrowserNotification } from './NotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TradieChatPopup() {
  const { user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [tradieProfile, setTradieProfile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevUnreadRef = useRef(0);

  // Check if user is a premium/business tradie
  useEffect(() => {
    if (user?.role === 'tradie') {
      fetchTradieProfile();
    }
  }, [user]);

  // Poll for new messages
  useEffect(() => {
    if (tradieProfile && ['premium', 'business'].includes(tradieProfile.membership_tier)) {
      fetchConversations();
      const interval = setInterval(fetchConversations, 3000);
      return () => clearInterval(interval);
    }
  }, [tradieProfile]);

  // Auto-popup when new messages arrive
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
      // New messages received - play sound and show notification
      playNotificationSound(0.6);
      
      if (minimized) {
        setMinimized(false); // Auto-popup
        
        toast.success('New customer message!', {
          description: 'A customer is trying to reach you',
          duration: 8000,
          action: {
            label: 'View',
            onClick: () => setMinimized(false)
          }
        });
        
        // Show browser notification if page not focused
        if (document.hidden) {
          showBrowserNotification(
            'New Customer Message',
            'A customer is trying to reach you on MyLocalTradie',
            () => {
              window.focus();
              setMinimized(false);
            }
          );
        }
      }
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, minimized]);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkYuBb2V1g46Xj4J0ZWx6ipOPg3VrbXyJkY2Cc2ttfImRjYJza218iZGNgnNrbXyJkY2Cc2ttfImRjYJza218iZGNgnNrbXyJkY2Cc2ttfImRjYJza218iZGNgnNrbXyJkY2Cc2ttfImRjYJza218iZGNgnNrbQ==');
      audio.volume = 0.5;
      audio.play().catch(() => { /* ignore audio play errors */ });
    } catch (e) {
      // Audio not supported
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTradieProfile = async () => {
    try {
      const response = await axios.get(`${API}/tradies/my/profile`, {
        withCredentials: true
      });
      setTradieProfile(response.data);
    } catch (error) {
      console.error('Error fetching tradie profile:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, {
        withCredentials: true
      });
      setConversations(response.data);
      
      // Calculate total unread
      const totalUnread = response.data.reduce((sum, conv) => sum + conv.unread_count, 0);
      setUnreadCount(totalUnread);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${conversationId}`, {
        withCredentials: true
      });
      setMessages(response.data);
      
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/chat/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setPendingAttachment({
        url: response.data.url,
        type: response.data.type,
        name: response.data.name
      });
      
      toast.success('File attached');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingAttachment) || !selectedConversation) return;

    setSending(true);
    try {
      await axios.post(`${API}/chat/send`, {
        receiver_id: selectedConversation.other_user.id,
        message: newMessage.trim() || `[Attachment: ${pendingAttachment?.name}]`,
        attachment_url: pendingAttachment?.url,
        attachment_type: pendingAttachment?.type,
        attachment_name: pendingAttachment?.name
      }, {
        withCredentials: true
      });

      const newMsg = {
        id: Date.now().toString(),
        sender_id: user.id,
        message: newMessage.trim() || `[Attachment: ${pendingAttachment?.name}]`,
        attachment_url: pendingAttachment?.url,
        attachment_type: pendingAttachment?.type,
        attachment_name: pendingAttachment?.name,
        created_at: new Date().toISOString(),
        is_read: false
      };
      setMessages([...messages, newMsg]);
      setNewMessage('');
      setPendingAttachment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const renderAttachment = (msg) => {
    if (!msg.attachment_url) return null;
    
    let fullUrl = msg.attachment_url;
    if (!fullUrl.startsWith('http')) {
      // Handle both old format (/uploads/...) and new format (/api/uploads/...)
      if (fullUrl.startsWith('/uploads/')) {
        fullUrl = `${BACKEND_URL}/api${fullUrl}`;
      } else {
        fullUrl = `${BACKEND_URL}${fullUrl}`;
      }
    }
    
    if (msg.attachment_type === 'image') {
      return (
        <div className="mt-2 rounded-lg overflow-hidden">
          <img 
            src={fullUrl} 
            alt={msg.attachment_name}
            className="max-w-full max-h-48 object-contain cursor-pointer hover:opacity-90"
            onClick={() => window.open(fullUrl, '_blank')}
          />
        </div>
      );
    }
    
    return (
      <a 
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 flex items-center gap-2 p-2 bg-white/10 rounded-lg hover:bg-white/20"
      >
        <FileText className="w-4 h-4" />
        <span className="text-sm truncate">{msg.attachment_name}</span>
        <Download className="w-4 h-4 ml-auto" />
      </a>
    );
  };

  // Don't render if not a premium/business tradie
  if (!user || user.role !== 'tradie') return null;
  if (!tradieProfile || !['premium', 'business'].includes(tradieProfile.membership_tier)) return null;

  // Minimized state - show floating button with unread count
  if (minimized) {
    return (
      <div className="fixed bottom-6 left-6 z-50" data-testid="tradie-chat-popup">
        <button
          onClick={() => setMinimized(false)}
          className="relative bg-mlt-success text-white p-4 rounded-full transition-all"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-mlt-danger text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-50" data-testid="tradie-chat-popup">
      <div className="bg-white rounded-xl border w-[calc(100vw-3rem)] sm:w-[420px] max-h-[550px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-mlt-success p-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5" />
              <div>
                <p className="font-semibold">Customer Messages</p>
                <p className="text-xs text-green-100">
                  {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setMinimized(true)} 
                className="hover:bg-white/20 p-1.5 rounded"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Conversations List */}
          <div className="w-1/3 border-r overflow-y-auto bg-mlt-off-white">
            {loading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-green-600 border-t-transparent mx-auto" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-mlt-text-muted text-sm">
                No conversations yet
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`w-full p-3 text-left border-b hover:bg-mlt-off-white transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id ? 'bg-mlt-success-light' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-mlt-border-strong rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {conv.other_user?.profile_photo ? (
                        <img 
                          src={conv.other_user.profile_photo.startsWith('http') ? conv.other_user.profile_photo : `${BACKEND_URL}${conv.other_user.profile_photo}`}
                          alt={conv.other_user?.name || 'Customer'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4 text-mlt-text-secondary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-mlt-navy text-sm truncate">
                        {conv.other_user?.name || 'Customer'}
                      </p>
                      <p className="text-xs text-mlt-text-muted truncate">
                        {((conv.last_message?.message || '').replace(/\s+/g, ' ').trim().slice(0, 80)) || 'No messages'}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="bg-mlt-danger text-white text-xs px-2 py-0.5 rounded-full">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Selected User Header */}
                <div className="p-3 border-b bg-white flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-mlt-border flex items-center justify-center overflow-hidden">
                    {selectedConversation.other_user?.profile_photo ? (
                      <img 
                        src={selectedConversation.other_user.profile_photo.startsWith('http') ? selectedConversation.other_user.profile_photo : `${BACKEND_URL}${selectedConversation.other_user.profile_photo}`}
                        alt={selectedConversation.other_user?.name || 'Customer'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-mlt-text-muted" />
                    )}
                  </div>
                  <span className="font-medium text-mlt-navy">
                    {selectedConversation.other_user?.name || 'Customer'}
                  </span>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2 bg-mlt-off-white max-h-[280px]">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === user.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex min-w-0 ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] min-w-0 rounded-xl px-3 py-2 ${
                            isOwn
                              ? 'bg-mlt-success text-white rounded-br-md'
                              : 'bg-white text-mlt-navy border rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.message}</p>
                          {renderAttachment(msg)}
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            isOwn ? 'text-green-100 justify-end' : 'text-mlt-text-muted'
                          }`}>
                            <span>
                              {new Date(msg.created_at).toLocaleTimeString('en-NZ', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {isOwn && (msg.is_read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Pending Attachment */}
                {pendingAttachment && (
                  <div className="px-3 py-2 bg-mlt-success-light border-t flex items-center gap-2">
                    {pendingAttachment.type === 'image' ? <Image className="w-4 h-4 text-mlt-success" /> : <FileText className="w-4 h-4 text-mlt-success" />}
                    <span className="text-sm text-mlt-success truncate flex-1">{pendingAttachment.name}</span>
                    <button onClick={() => setPendingAttachment(null)} className="text-mlt-success hover:text-mlt-success">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-3 border-t bg-white">
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-2"
                    >
                      {uploading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent" />
                      ) : (
                        <Paperclip className="w-4 h-4 text-mlt-text-muted" />
                      )}
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Reply..."
                      className="flex-1 rounded-full text-sm"
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      disabled={(!newMessage.trim() && !pendingAttachment) || sending}
                      className="bg-mlt-success hover:bg-green-700 rounded-full w-9 h-9 p-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-mlt-text-muted">
                <div className="text-center p-4">
                  <MessageCircle className="w-12 h-12 text-mlt-text-muted mx-auto mb-3" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm">Click on a customer to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
